import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
  type Ref,
} from 'react'
import classNames from 'classnames'
import { TEXT_FIELD_LIMIT_WARNING_MS } from 'shared/ui/TextField/ActionsMain/TextFieldHighlightLimit'
import { toastStore } from 'shared/ui/Toast'
import { Error } from 'shared/ui/Error'
import { IconButton } from 'shared/ui/IconButton'
import { type ILabelProps, Label } from 'shared/ui/Label/Label'
import { Warning } from 'shared/ui/Warning'
import { Textarea } from 'shared/ui/Textarea/Textarea'
import styles from './styles.module.scss'

export type IErrorType = 'toast' | 'bottomText'

const LIMIT_TRIM_DELAY_MS = 500

const resizeTextareaByScrollHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}

export type ITextareaWithLabelProps = {
  name?: string
  placeholder?: string
  labelProps: ILabelProps
  error?: string | null
  isError?: boolean
  value: string
  limit?: number
  minLimit?: number
  trimOnLimit?: boolean
  rows?: number
  autoResize?: boolean
  errorType?: IErrorType
  className?: string
  onBlur?: (value: string) => void
  onChange: (value: string) => void
  classNameTextarea?: string
  shouldResetErrors?: boolean
  validationMessage?: string
  overlayNode?: ReactNode
  resetAction?: {
    isVisible: boolean
    onReset: () => void
    ariaLabel?: string
    tooltipLabel?: string
  }
  bottomNode?: {
    leftNode?: ReactNode
    rightNode?: ReactNode
  }
}

export const TextareaWithLabelRef = (
  {
    name = 'message',
    placeholder,
    labelProps,
    error,
    isError = false,
    value,
    limit,
    minLimit,
    trimOnLimit = false,
    rows = 4,
    autoResize = false,
    errorType = 'bottomText',
    className,
    onBlur,
    onChange,
    classNameTextarea,
    shouldResetErrors = false,
    bottomNode,
    validationMessage,
    overlayNode,
    resetAction,
  }: ITextareaWithLabelProps,
  ref: Ref<HTMLTextAreaElement>
) => {
  const isDirty = useRef(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const limitWarningTimerRef = useRef<number>(-1)
  const trimTimerRef = useRef<number>(-1)
  const valueRef = useRef(value)
  const limitToastArmedRef = useRef(true)

  valueRef.current = value
  const [limitWarningVisible, setLimitWarningVisible] = useState(false)
  const [minLengthError, setMinLengthError] = useState('')

  const setRef = (el: HTMLTextAreaElement | null) => {
    const mutableTextareaRef = textareaRef as MutableRefObject<HTMLTextAreaElement | null>
    mutableTextareaRef.current = el
    if (ref) {
      if (typeof ref === 'function') ref(el)
      else (ref as MutableRefObject<HTMLTextAreaElement | null>).current = el
    }
  }

  useLayoutEffect(() => {
    if (autoResize) resizeTextareaByScrollHeight(textareaRef.current)
  }, [autoResize, value])

  const getLimitLengthMessage = () => {
    if (limit === null || limit === undefined) return null
    return `Cannot be longer than ${limit} characters`
  }

  const handleChange = (value: string) => {
    isDirty.current = true
    onChange(value)
    setMinLengthError('')

    const overLimit = limit !== null && limit !== undefined && value.length > limit
    const limitLengthMessage = getLimitLengthMessage()

    if (overLimit) {
      if (errorType === 'bottomText') {
        setLimitWarningVisible(true)
        window.clearTimeout(limitWarningTimerRef.current)
        limitWarningTimerRef.current = window.setTimeout(() => {
          setLimitWarningVisible(false)
        }, TEXT_FIELD_LIMIT_WARNING_MS)
      }

      if (errorType === 'toast' && limitLengthMessage && limitToastArmedRef.current) {
        toastStore.add({
          title: limitLengthMessage,
          type: 'error',
        })
        limitToastArmedRef.current = false
      }

      if (trimOnLimit && limit !== null && limit !== undefined) {
        const maxLength = limit
        window.clearTimeout(trimTimerRef.current)
        trimTimerRef.current = window.setTimeout(() => {
          if (valueRef.current.length > maxLength) {
            onChange(valueRef.current.slice(0, maxLength))
          }
          setLimitWarningVisible(false)
        }, LIMIT_TRIM_DELAY_MS)
      }
    } else {
      window.clearTimeout(limitWarningTimerRef.current)
      window.clearTimeout(trimTimerRef.current)
      limitToastArmedRef.current = true
      if (errorType === 'bottomText') {
        setLimitWarningVisible(false)
      }
    }
  }

  useEffect(() => {
    if (shouldResetErrors) {
      setMinLengthError('')
      isDirty.current = true
    }
  }, [shouldResetErrors])

  const handleBlur = (value: string) => {
    let nextValue = value

    if (trimOnLimit && limit !== null && limit !== undefined && value.length > limit) {
      window.clearTimeout(trimTimerRef.current)
      nextValue = value.slice(0, limit)
      onChange(nextValue)
      setLimitWarningVisible(false)
    }

    onBlur?.(nextValue)

    if (minLimit && nextValue.length < minLimit) {
      setMinLengthError(validationMessage || `The ${name} must be at least ${minLimit} characters`)
    }
  }

  useEffect(() => {
    return () => {
      window.clearTimeout(limitWarningTimerRef.current)
      window.clearTimeout(trimTimerRef.current)
    }
  }, [])

  const showLimitWarning = errorType === 'bottomText' && limitWarningVisible
  const isShowBottomError = error || minLengthError

  const resetOverlay =
    resetAction && resetAction.isVisible ? (
      <div className={styles.resetButton}>
        <IconButton
          iconProps={{ fontSize: 20 }}
          variant={'icon'}
          color={'tertiary'}
          size={'medium'}
          icon={'back'}
          ariaLabel={resetAction.ariaLabel ?? 'Reset'}
          onClick={resetAction.onReset}
          tooltipProps={{
            label: resetAction.tooltipLabel ?? 'Reset to default',
            placement: 'top',
          }}
        />
      </div>
    ) : null

  return (
    <div className={classNames(styles.textareaWrap, className)}>
      {labelProps.label && <Label marginBottom={4} {...labelProps} />}

      <Textarea
        ref={setRef}
        rows={rows}
        placeholder={placeholder}
        error={!!error || !!minLengthError || isError}
        limitWarning={showLimitWarning && !error && !minLengthError && !isError}
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        className={classNameTextarea}
        overlayNode={resetOverlay ?? overlayNode}
      />

      {isShowBottomError && <Error type='text' error={error || minLengthError} size='small' />}
      {showLimitWarning && (
        <Warning text={getLimitLengthMessage() ?? ''} className={styles.warning} />
      )}
      {bottomNode && (
        <div className={styles.bottomNode}>
          {bottomNode.leftNode} {bottomNode.rightNode}
        </div>
      )}
    </div>
  )
}

export const TextareaWithLabel = forwardRef(TextareaWithLabelRef)
